/**
 * ParlayBuilderPanel Component
 * Bottom-docked panel for building custom parlays
 * Shows selected legs, warnings, scoring, and track action
 */

import { SPORTSBOOKS } from '@config/constants';
import { useAuth } from '@contexts/AuthContext';
import { useQuotaCoin } from '@contexts/QuotaCoinContext';
import { useToast } from '@contexts/ToastContext';
import { useChartData } from '@hooks/useChartData';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useParlayScoring } from '@hooks/useParlayScoring';
import type { ScoringParlay } from '@hooks/useStrategyStats';
import type { NewParlay, TrackParlayResult } from '@hooks/useTrackedParlays';
import { preferredInSeasonLeagueKey } from '@parlay-picker/shared/league';
import { deriveStoredRecommendation } from '@parlay-picker/shared/recommendation';
import { DEFAULT_RAW_THRESHOLDS } from '@parlay-picker/shared/scoreDisplay';
import { marketKeyToChartPeriod } from '@parlay-picker/shared/statsChart';
import { useChatIsOpen } from '@stores/chatStore';
import { useGenerationStore } from '@stores/generationStore';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { useOptimizerStore } from '@stores/optimizerStore';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { cn } from '@utils/cn';
import type { ChartClickData } from '@views/picks/components/ParlayCard';
import { ScorePanel } from '@views/picks/components/ScorePanel';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronUp, FileText, Loader2, Plus } from 'lucide-react';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { StatsChartModal } from '../ui';
import GameStatsChartModal from '../ui/GameStatsChartModal';
import { BuilderLegRow } from './BuilderLegRow';
import { PairSuggestions } from './PairSuggestions';
import { ParlayAnalysisSection } from './ParlayAnalysisSection';
import { PropBrowserModal } from './PropBrowserModal';
import { SgpRulesNote } from './SgpRulesNote';

// =============================================================================
// Types
// =============================================================================

interface ParlayBuilderPanelProps {
  /** Track parlay function from parent (ensures state sync) */
  trackParlay: (parlay: NewParlay) => Promise<TrackParlayResult>;
  /** Callback after successful track */
  onTrackSuccess?: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function ParlayBuilderPanel({
  trackParlay,
  onTrackSuccess,
}: ParlayBuilderPanelProps): ReactElement | null {
  const { authFetch, user } = useAuth();
  const { showCoin } = useQuotaCoin();
  const { showToast } = useToast();
  const [isTracking, setIsTracking] = useState(false);

  // Hide when picks config overlay is fullscreen
  const isPicksConfigFullscreen = useGlobalShortcutsStore((s) => s.isPicksConfigFullscreen);
  const isChatOpen = useChatIsOpen();

  // The prop browser opens on the last generation's league, else on preferredInSeasonLeagueKey's
  const generationLeagueKey = useGenerationStore((s) => s.generationLeagueKey);
  const propBrowserLeague =
    generationLeagueKey ?? preferredInSeasonLeagueKey(user?.preferences?.preferredLeague);

  // Chart data fetching hook
  const { fetchTeamStats, fetchPlayerStats } = useChartData();

  // Zustand store - selective subscriptions for better performance
  const legs = useParlayBuilderStore((s) => s.legs);
  const isExpanded = useParlayBuilderStore((s) => s.isExpanded);
  const analysis = useParlayBuilderStore((s) => s.analysis);
  const isAnalyzing = useParlayBuilderStore((s) => s.isAnalyzing);
  const analysisError = useParlayBuilderStore((s) => s.analysisError);

  // Store actions (stable references, don't cause re-renders)
  const {
    toggleExpanded,
    removeLeg,
    clearAll,
    buildTrackPayload,
    analyzeParlay,
    clearAnalysis,
    applySuggestion,
    getSportsbook,
    getMaxLegs,
    getTotalOdds,
    getLegCount,
    getWarnings,
    getStartedLegs,
  } = useParlayBuilderStore();

  // Derived values (computed on access)
  const sportsbook = getSportsbook();
  const maxLegs = getMaxLegs();
  const totalOdds = getTotalOdds();
  const legCount = getLegCount();
  const warnings = getWarnings();
  const startedLegs = getStartedLegs();
  const hasStartedGames = startedLegs.length > 0;

  // Score via server-side API with debouncing for responsive editing
  const {
    scores,
    loading: scoringLoading,
    scoreParlays,
    clearScores,
  } = useParlayScoring({ debounceMs: 500 });

  // Convert builder legs to scoring format
  const scoringParlay = useMemo((): ScoringParlay | null => {
    if (legs.length === 0) return null;

    // Use the original AI parlay name for tier-based scoring when legs came from AI
    const aiTiers = legs.map((l) => l.aiTier).filter(Boolean);
    const parlayName = aiTiers.length > 0 ? aiTiers[0]! : 'Custom Parlay';

    // Get parlay-level confidence from first leg (all legs from same AI parlay share confidence)
    const aiConfidences = legs.map((l) => l.aiConfidence).filter((c): c is string => Boolean(c));
    const parlayConfidence = aiConfidences.length > 0 ? aiConfidences[0] : undefined;

    return {
      name: parlayName,
      confidence: parlayConfidence,
      legs: legs.map((leg) => ({
        player: leg.player ?? undefined,
        team: leg.team,
        propType: leg.statType,
        statType: leg.statType,
        line: leg.line ?? undefined,
        overUnder: leg.overUnder ?? undefined,
        direction: leg.overUnder ?? undefined,
        edge: leg.aiEdge ?? undefined,
        confidence: leg.aiConfidence ?? undefined,
        opponent: leg.opponentTeam ?? undefined,
        isB2b: leg.isB2b ?? undefined,
        blowoutRisk: leg.blowoutRisk ?? undefined,
        isHomeGame: leg.isHomeGame ?? null,
        gameId: leg.gameId ?? undefined,
        leagueKey: leg.leagueKey,
      })),
    };
  }, [legs]);

  // Trigger scoring when parlay changes
  useEffect(() => {
    if (scoringParlay) {
      scoreParlays([scoringParlay]);
    } else {
      clearScores();
    }
  }, [scoringParlay, scoreParlays, clearScores]);

  const userScore = scores[0]?.strategyScore ?? null;
  const aiScore = scores[0]?.predictionScore ?? null;

  const { isHidden } = useFeatureAccess();
  const openOptimizer = useOptimizerStore((s) => s.openWithLegs);

  // Modal state
  const [isPropBrowserOpen, setIsPropBrowserOpen] = useState(false);
  const [propBrowserInitialTab, setPropBrowserInitialTab] = useState<'browse' | 'import'>('browse');
  const [chartModal, setChartModal] = useState<ChartClickData | null>(null);
  const [_chartLoading, setChartLoading] = useState(false);

  // Handle chart click from leg rows - fetches data on-demand if not already present
  const handleChartClick = async (data: ChartClickData): Promise<void> => {
    // If data already has stats, show modal immediately
    const hasPlayerData = data.recentGames && data.recentGames.length > 0;
    const hasTeamData = data.teamRecentGames && data.teamRecentGames.length > 0;

    if (hasPlayerData || hasTeamData) {
      setChartModal(data);
      return;
    }

    // Need to fetch data
    setChartLoading(true);

    try {
      if (data.isGameProp && data.teamName) {
        // Fetch team stats using hook
        const period = marketKeyToChartPeriod(data.marketKey);
        const teamData = await fetchTeamStats(
          data.teamName,
          data.leagueKey,
          period !== 'full' ? period : undefined,
        );

        setChartModal({
          ...data,
          teamRecentGames: teamData.recentGames || [],
        });
      } else if (data.playerName) {
        // Fetch player stats with optional opponent using hook
        const playerPeriod = marketKeyToChartPeriod(data.marketKey);
        const playerData = await fetchPlayerStats(
          data.playerName,
          data.leagueKey,
          data.opponentAbbr,
          playerPeriod !== 'full' ? playerPeriod : undefined,
        );

        setChartModal({
          ...data,
          recentGames: playerData.recentGames || [],
          h2hGames: playerData.h2hGames || undefined,
        });
      } else {
      }
    } catch (_err) {
      // Still show modal even if fetch failed - it will show "no data" state
      setChartModal(data);
    } finally {
      setChartLoading(false);
    }
  };

  const handleTrack = async (): Promise<void> => {
    setIsTracking(true);
    try {
      const payload = buildTrackPayload();
      if (!payload) {
        showToast('Add a leg with a sportsbook before tracking', 'error');
        return;
      }

      // Attach current scores to the payload (locked at track time)
      if (userScore || aiScore) {
        // Convert ScoringFactor[] to Record<string, number> for storage
        const factorsToRecord = (
          factors: Array<{ factor: string; score: number }> | undefined,
        ): Record<string, number> => {
          if (!factors) return {};
          const record: Record<string, number> = {};
          for (const f of factors) {
            record[f.factor] = f.score;
          }
          return record;
        };

        payload.trackedStrategyScore = userScore?.score;
        payload.trackedPredictionScore = aiScore?.score;
        payload.trackedStrategyRec =
          userScore?.score != null
            ? deriveStoredRecommendation(userScore.score, userScore.thresholds)
            : undefined;
        payload.trackedPredictionRec =
          aiScore?.score != null
            ? deriveStoredRecommendation(aiScore.score, aiScore.thresholds)
            : undefined;
        // Store scoreReliability for calibration analysis (not shown to users, admin only)
        payload.scoreReliability = userScore?.scoreReliability ?? aiScore?.scoreReliability ?? null;
        payload.trackedScoreBreakdown = {
          strategy: {
            total: userScore?.score ?? 0,
            recommendation:
              userScore?.score != null
                ? deriveStoredRecommendation(userScore.score, userScore.thresholds)
                : '',
            factors: factorsToRecord(userScore?.factors),
          },
          prediction: {
            total: aiScore?.score ?? 0,
            recommendation:
              aiScore?.score != null
                ? deriveStoredRecommendation(aiScore.score, aiScore.thresholds)
                : 'SKIP',
            factors: factorsToRecord(aiScore?.factors),
          },
          thresholds: {
            bet:
              userScore?.thresholds?.bet ?? aiScore?.thresholds?.bet ?? DEFAULT_RAW_THRESHOLDS.bet,
            skip:
              userScore?.thresholds?.skip ??
              aiScore?.thresholds?.skip ??
              DEFAULT_RAW_THRESHOLDS.skip,
          },
        };
      }

      const result = await trackParlay(payload);

      if (result.success) {
        if (result.quotaData) showCoin(result.quotaData);
        clearAll();
        onTrackSuccess?.();
      } else if (result.error) {
        // Show specific error message (e.g., "Game already started")
        showToast(result.error, 'error');
      }
    } finally {
      setIsTracking(false);
    }
  };

  // Shared expanded content renderer (used by both rebrand AnimatePresence and legacy conditional)
  const renderExpandedContent = (): ReactElement => (
    <>
      {/* Started Games Warning */}
      {hasStartedGames && (
        <div className="mb-3 flex items-center gap-2 text-sm px-3 py-2 rounded border text-theme-danger bg-theme-danger/10 border-theme-danger/30">
          <span>🚫</span>
          <span>
            {startedLegs.length === 1
              ? `1 game has already started (${startedLegs[0].player || startedLegs[0].team})`
              : `${startedLegs.length} games have already started`}
            . Remove affected legs to track.
          </span>
        </div>
      )}

      {/* Correlation Warnings */}
      {warnings.length > 0 && (
        <div className="mb-3 space-y-1">
          {warnings.map((warning, i) => (
            <div
              key={i}
              className={cn(
                'flex items-center gap-2 text-sm px-2 py-1.5 rounded',
                warning.severity === 'medium'
                  ? 'text-orange-400 bg-orange-500/10'
                  : 'text-theme-warning bg-theme-warning/10',
              )}
            >
              <span>⚠️</span>
              <span>{warning.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Legs list — scrollable when many legs */}
      <div className="space-y-2 mb-3 max-h-[30vh] overflow-y-auto">
        {legs.map((leg, index) => {
          const grade = analysis?.legGrades?.find((g) => g.legIndex === index);
          return (
            <BuilderLegRow
              key={leg.id}
              leg={leg}
              index={index}
              onRemove={() => removeLeg(leg.id)}
              disabled={isAnalyzing}
              onChartClick={handleChartClick}
              analysisGrade={grade ? { grade: grade.grade, note: grade.note } : undefined}
            />
          );
        })}
      </div>

      {/* Phase 9.3: Pair suggestions */}
      <PairSuggestions legs={legs} />

      {/* SGP rules info (Phase 9.8) */}
      <SgpRulesNote legs={legs} sportsbook={sportsbook} />

      {/* AI Analysis Section */}
      {analysis && (
        <ParlayAnalysisSection
          analysis={analysis}
          legs={legs}
          onApplySuggestion={(suggestion) => {
            applySuggestion(suggestion);
            showToast('Parlay updated — re-analyze to see new verdict', 'info', 4000);
          }}
          onClose={clearAnalysis}
        />
      )}

      {/* Analysis Error */}
      {analysisError && !analysis && (
        <div className="mb-3 flex items-center gap-2 text-sm px-3 py-2 rounded border text-theme-danger bg-theme-danger/10 border-theme-danger/30">
          <span>Failed to analyze:</span>
          <span>{analysisError}</span>
          <button
            type="button"
            onClick={() => analyzeParlay(authFetch, showCoin)}
            className="ml-auto text-xs underline text-theme-danger hover:text-theme-text"
          >
            Retry
          </button>
        </div>
      )}

      {/* Action buttons row */}
      <div className="flex items-center gap-2 pt-2 border-t border-theme-border">
        <button
          type="button"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition ${
            isAnalyzing
              ? 'text-theme-text-tertiary cursor-not-allowed'
              : 'text-theme-text-secondary hover:text-white hover:bg-theme-border'
          }`}
          onClick={() => {
            setPropBrowserInitialTab('browse');
            setIsPropBrowserOpen(true);
          }}
          disabled={isAnalyzing}
          title={isAnalyzing ? 'Wait for analysis to complete' : undefined}
        >
          <Plus className="w-4 h-4" />
          Add More Props
        </button>

        <button
          type="button"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition ${
            isAnalyzing
              ? 'text-theme-text-tertiary cursor-not-allowed'
              : 'text-theme-text-secondary hover:text-white hover:bg-theme-border'
          }`}
          onClick={() => {
            setPropBrowserInitialTab('import');
            setIsPropBrowserOpen(true);
          }}
          disabled={isAnalyzing}
          title={isAnalyzing ? 'Wait for analysis to complete' : undefined}
        >
          <FileText className="w-4 h-4" />
          Import
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Analyze button */}
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition',
            isAnalyzing
              ? 'bg-theme-primary/70 text-theme-text-secondary cursor-wait'
              : analysis
                ? 'bg-theme-primary/30 text-theme-text-tertiary cursor-not-allowed'
                : 'bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill',
          )}
          onClick={() => analyzeParlay(authFetch, showCoin)}
          disabled={isAnalyzing || !!analysis}
          title={
            analysis
              ? 'Already analyzed - modify parlay to re-analyze'
              : 'Get AI analysis of this parlay'
          }
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <span>🤖</span>
              {analysis ? 'Re-analyze' : 'Analyze'}
            </>
          )}
        </button>
      </div>
    </>
  );

  // Hide everything when picks config overlay is fullscreen
  if (isPicksConfigFullscreen) return null;

  // Render floating button when empty, or full panel when has legs
  // Modal is always rendered at the end to maintain consistent lifecycle
  return (
    <>
      {legCount === 0 ? (
        /* Floating Action Button to start building */
        <motion.button
          type="button"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          onClick={() => setIsPropBrowserOpen(true)}
          className={cn(
            'fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill rounded-full shadow-lg shadow-theme-primary/25 hover:shadow-xl hover:shadow-theme-primary/40 transition-all duration-300',
            isChatOpen && 'md:right-[444px]',
          )}
          title="Build a custom parlay"
        >
          <Plus className="w-5 h-5" />
          <span className="font-medium">Build Parlay</span>
        </motion.button>
      ) : (
        <div
          className={cn(
            'fixed inset-x-0 bottom-0 z-40 pointer-events-none transition-[right] duration-300',
            isChatOpen && 'md:right-[420px]',
          )}
        >
          {/* Gradient fade background */}
          <div className="pointer-events-none bg-gradient-to-t from-theme-bg/95 to-transparent h-8" />

          {/* Panel container */}
          <div
            data-testid="parlay-builder-panel"
            className="pointer-events-auto shadow-2xl pb-[env(safe-area-inset-bottom)] rebrand-builder-panel rounded-t-2xl relative"
          >
            {/* Header bar - always visible */}
            <div
              data-testid="parlay-builder-header"
              data-expanded={isExpanded ? 'true' : 'false'}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 cursor-pointer transition hover:bg-white/5"
              onClick={toggleExpanded}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                {/* Hammer icon */}
                <span className="text-lg">🔨</span>

                {/* Leg count */}
                <span className="font-medium text-theme-text">Building Parlay</span>
                <span
                  className={`text-sm ${legCount >= maxLegs ? 'text-orange-400' : 'text-theme-text-tertiary'}`}
                >
                  {legCount}/{maxLegs}
                </span>

                {/* Sportsbook badge */}
                {sportsbook && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${SPORTSBOOKS[sportsbook]?.bg || 'bg-theme-primary/15'} ${SPORTSBOOKS[sportsbook]?.text || 'text-theme-primary'}`}
                  >
                    {SPORTSBOOKS[sportsbook]?.name || sportsbook}
                  </span>
                )}

                {/* Total odds + implied probability (Phase 9.6) */}
                {totalOdds && (
                  <span
                    className="font-medium text-theme-success"
                    title={(() => {
                      const odds = parseInt(totalOdds.replace('+', ''), 10);
                      if (!Number.isFinite(odds)) return '';
                      const prob =
                        odds > 0 ? 100 / (odds + 100) : Math.abs(odds) / (Math.abs(odds) + 100);
                      return `${(prob * 100).toFixed(1)}% implied probability`;
                    })()}
                  >
                    {totalOdds}
                    {(() => {
                      const odds = parseInt(totalOdds.replace('+', ''), 10);
                      if (!Number.isFinite(odds)) return null;
                      const prob =
                        odds > 0 ? 100 / (odds + 100) : Math.abs(odds) / (Math.abs(odds) + 100);
                      return (
                        <span className="text-[10px] text-theme-text-tertiary ml-1">
                          ({(prob * 100).toFixed(0)}%)
                        </span>
                      );
                    })()}
                  </span>
                )}

                {/* Score badges with hover tooltips - reuse ScorePanel */}
                {scoringLoading ? (
                  <span className="text-[10px] text-theme-text-tertiary animate-pulse">
                    Scoring...
                  </span>
                ) : userScore || aiScore ? (
                  <motion.div
                    key={`score-${userScore?.score ?? 'none'}-${aiScore?.score ?? 'none'}`}
                    initial={{ scale: 0.9, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="flex items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ScorePanel
                      dynamicScore={userScore}
                      predictionScore={aiScore}
                      legs={legs}
                      className=""
                    />
                  </motion.div>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                {/* Warning indicator */}
                {warnings.length > 0 && (
                  <span
                    className="text-sm text-theme-warning"
                    title={warnings.map((w) => w.message).join('\n')}
                  >
                    ⚠️ {warnings.length}
                  </span>
                )}

                {/* Clear button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  disabled={isAnalyzing}
                  className={`px-3 py-1 text-sm rounded transition ${
                    isAnalyzing
                      ? 'text-theme-text-tertiary cursor-not-allowed'
                      : 'text-theme-text-secondary hover:text-white hover:bg-theme-border'
                  }`}
                >
                  Clear
                </button>

                {/* Structure recommendation (Phase 9.4) */}
                {legCount >= 4 && !isHidden('ml_parlay_optimizer') && (
                  <span className="text-[10px] text-purple-400/70">
                    {legCount <= 5
                      ? `${Math.max(1, legCount - 1)} two-leg parlays`
                      : legCount <= 7
                        ? `${legCount - 2} two-leg + 1 three-leg`
                        : `${legCount - 3} two-leg + 2 three-leg`}
                  </span>
                )}

                {legCount >= 4 && !isHidden('ml_parlay_optimizer') && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openOptimizer(
                        legs.map((l, i) => ({
                          id: String(i),
                          player: l.player ?? '',
                          team: l.team ?? '',
                          statType: l.statType ?? '',
                          line: l.line ?? null,
                          overUnder: l.overUnder ?? null,
                          odds: l.odds ?? '-110',
                          gameId: l.gameId ?? '',
                          score: 0,
                          sportsbook: sportsbook ?? 'fanduel',
                          leagueKey: l.leagueKey,
                          provenance: l.provenance,
                          predictionId: l.predictionId,
                          factorsUsed: l.factorsUsed,
                        })),
                      );
                    }}
                    className="px-3 py-1 text-xs font-medium rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors"
                  >
                    Optimize
                  </button>
                )}

                {/* Track button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTrack();
                  }}
                  disabled={isTracking || isAnalyzing || hasStartedGames}
                  className="px-4 py-1 text-sm font-medium text-white rounded transition bg-theme-secondary hover:bg-theme-secondary/80 disabled:bg-theme-surface disabled:text-theme-text-tertiary shadow-sm shadow-theme-secondary/20"
                  title={
                    hasStartedGames
                      ? `${startedLegs.length} game(s) already started`
                      : isAnalyzing
                        ? 'Wait for analysis to complete'
                        : undefined
                  }
                >
                  {isTracking
                    ? 'Tracking...'
                    : hasStartedGames
                      ? 'Game Started'
                      : legCount === 1
                        ? 'Track Bet'
                        : 'Track Parlay'}
                </button>

                {/* Expand/Collapse chevron */}
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                >
                  <ChevronUp className="w-5 h-5 text-theme-text-tertiary" />
                </motion.div>
              </div>
            </div>

            {/* Expandable content */}
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  key="builder-content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 max-h-[40vh] sm:max-h-[50vh] overflow-y-auto">
                    {renderExpandedContent()}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Full-panel loading overlay while tracking */}
            <AnimatePresence>
              {isTracking && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="absolute inset-0 z-10 flex items-center justify-center rounded-t-2xl bg-theme-bg/80 backdrop-blur-sm"
                >
                  <div className="flex items-center gap-3 text-theme-text">
                    <Loader2 className="w-5 h-5 animate-spin text-theme-secondary" />
                    <span className="font-medium">Tracking parlay...</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Prop Browser Modal - rendered outside conditional for consistent lifecycle */}
      <PropBrowserModal
        isOpen={isPropBrowserOpen}
        onClose={() => setIsPropBrowserOpen(false)}
        initialTab={propBrowserInitialTab}
        defaultLeague={propBrowserLeague}
      />

      {/* Stats Chart Modal - conditionally render player or game modal */}
      {chartModal?.isGameProp && (
        <GameStatsChartModal
          key={`${chartModal.teamName}-${chartModal.propLine}-${chartModal.propType}`}
          leagueKey={chartModal.leagueKey}
          onClose={() => setChartModal(null)}
          teamName={chartModal.teamName ?? ''}
          prop={chartModal.prop ?? ''}
          recentGames={chartModal.teamRecentGames}
          h2hGames={chartModal.teamH2hGames}
          propLine={chartModal.propLine ?? undefined}
          propType={chartModal.propType ?? 'spread'}
          opponentAbbr={chartModal.opponentAbbr}
          overUnder={chartModal.overUnder}
          opponentTeamName={chartModal.opponentTeamName}
          opponentTeamRecentGames={chartModal.opponentTeamRecentGames}
        />
      )}
      {chartModal && !chartModal.isGameProp && (
        <StatsChartModal
          key={`${chartModal.playerName}-${chartModal.propLine}-${chartModal.statKey}`}
          leagueKey={chartModal.leagueKey}
          onClose={() => setChartModal(null)}
          playerName={chartModal.playerName ?? ''}
          prop={chartModal.prop ?? ''}
          recentGames={chartModal.recentGames}
          h2hGames={chartModal.h2hGames}
          propLine={chartModal.propLine ?? undefined}
          statKey={chartModal.statKey ?? undefined}
          opponentAbbr={chartModal.opponentAbbr}
          overUnder={chartModal.overUnder}
          teamName={chartModal.playerTeamName}
        />
      )}
    </>
  );
}
